import { z } from 'zod';
import {
  KPI_AGGREGATIONS,
  KPI_DATA_SOURCES,
  KPI_DIRECTIONS,
  KPI_KINDS,
  KPI_PERIODICITIES,
  KPI_TRAFFIC_MODES,
  KPI_UNITS,
} from './kpis';
import { OBJECTIVE_HORIZONS, OBJECTIVE_RELATION_TYPES } from './objectives';

/**
 * Modelos de projeto BSC (T-204, RF-50; DEC-070): a estrutura de um projeto, sem dados de cliente, para abrir
 * projetos novos a partir dela. Os itens se referem uns aos outros por `key` (nunca pelos ids do projeto de origem).
 */
const key = z.string().min(1).max(64);
const weight = z.number().min(0).max(100).nullable();
const decimal = z.number().nullable();

export const templatePerspectiveSchema = z.object({
  key,
  name: z.string().min(1),
  description: z.string().nullable(),
  weight,
  position: z.number().int(),
});

export const templateObjectiveSchema = z.object({
  key,
  perspectiveKey: key,
  name: z.string().min(1),
  description: z.string().nullable(),
  horizon: z.enum(OBJECTIVE_HORIZONS),
  weight,
  position: z.number().int(),
});

export const templateRelationSchema = z.object({
  fromKey: key,
  toKey: key,
  type: z.enum(OBJECTIVE_RELATION_TYPES),
});

export const templateCustomUnitSchema = z.object({
  key,
  name: z.string().min(1),
  symbol: z.string().nullable(),
  aggregation: z.enum(KPI_AGGREGATIONS),
});

export const templateKpiSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  kind: z.enum(KPI_KINDS),
  perspectiveKey: key,
  unit: z.enum(KPI_UNITS),
  customUnitKey: key.nullable(),
  periodicity: z.enum(KPI_PERIODICITIES),
  customPeriodDays: z.number().int().nullable(),
  dataSource: z.enum(KPI_DATA_SOURCES),
  direction: z.enum(KPI_DIRECTIONS),
  rangeLower: decimal,
  rangeUpper: decimal,
  performanceFormula: z.string().nullable(),
  calculationFormula: z.string().nullable(),
  formula: z.string(),
  aggregation: z.enum(KPI_AGGREGATIONS),
  trafficMode: z.enum(KPI_TRAFFIC_MODES),
  greenMin: decimal,
  yellowMin: decimal,
  objectives: z.array(z.object({ objectiveKey: key, weight })),
});

export const projectTemplateContentSchema = z.object({
  perspectives: z.array(templatePerspectiveSchema),
  objectives: z.array(templateObjectiveSchema),
  relations: z.array(templateRelationSchema),
  customUnits: z.array(templateCustomUnitSchema),
  kpis: z.array(templateKpiSchema),
});
export type ProjectTemplateContent = z.infer<typeof projectTemplateContentSchema>;

export const projectTemplateSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  /** De onde veio: empresa e projeto na hora da cópia (null para quem não tem acesso à empresa de origem). */
  source: z.object({ organization: z.string(), project: z.string() }).nullable(),
  counts: z.object({
    perspectives: z.number().int(),
    objectives: z.number().int(),
    kpis: z.number().int(),
  }),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectTemplateSummary = z.infer<typeof projectTemplateSummarySchema>;

export const projectTemplateListSchema = z.object({ items: z.array(projectTemplateSummarySchema) });
export type ProjectTemplateList = z.infer<typeof projectTemplateListSchema>;

export const projectTemplateSchema = projectTemplateSummarySchema.extend({
  content: projectTemplateContentSchema,
});
export type ProjectTemplate = z.infer<typeof projectTemplateSchema>;

export const PROJECT_TEMPLATE_NAME_MAX = 120;
export const PROJECT_TEMPLATE_DESCRIPTION_MAX = 500;

/** Nome e descrição do modelo (criar e renomear). */
export const projectTemplateInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'Informe o nome do modelo (pelo menos 3 letras).')
    .max(PROJECT_TEMPLATE_NAME_MAX, 'Nome longo demais.'),
  description: z
    .string()
    .trim()
    .max(
      PROJECT_TEMPLATE_DESCRIPTION_MAX,
      `A descrição pode ter no máximo ${PROJECT_TEMPLATE_DESCRIPTION_MAX} caracteres.`,
    )
    .nullish()
    .transform((v) => v || null),
});
export type ProjectTemplateInput = z.input<typeof projectTemplateInputSchema>;

/**
 * Abrir o projeto a partir de um modelo: o modelo e, quando ele tem KPIs, a área e o responsável que os KPIs
 * recebem (ajustáveis depois em cada KPI).
 */
export const projectTemplateApplySchema = z.object({
  templateId: z.string().uuid('Modelo inválido.'),
  kpiDepartmentId: z.string().uuid('Área inválida.').nullish(),
  kpiOwnerId: z.string().uuid('Responsável inválido.').nullish(),
});
export type ProjectTemplateApply = z.input<typeof projectTemplateApplySchema>;

/** Opções para abrir projeto a partir de modelo: áreas e pessoas ativas da empresa (área e responsável dos KPIs). */
export const projectOptionsSchema = z.object({
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  people: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
export type ProjectOptions = z.infer<typeof projectOptionsSchema>;
